import type { ReactNode } from "react";

import { Icon } from "@/components/hifi/Icon";

export interface TopbarTab {
  key: string;
  label: string;
  count?: number;
}

export interface TopbarProps {
  crumbs?: ReactNode[];
  title: ReactNode;
  subtitle?: ReactNode;
  right?: ReactNode;
  tabs?: TopbarTab[];
  activeTab?: string;
  onTabChange?: (key: string) => void;
}

export function Topbar({
  crumbs = [],
  title,
  subtitle,
  right,
  tabs,
  activeTab,
  onTabChange,
}: TopbarProps): ReactNode {
  return (
    <header className="flex-shrink-0 border-b border-n-150 bg-white px-5">
      <div className="flex items-center gap-4 pt-3 pb-2.5">
        <div className="min-w-0 flex-1">
          {crumbs.length > 0 && (
            <div className="mb-0.5 flex items-center gap-1 text-[11px] text-n-500">
              {crumbs.map((c, i) => (
                <span key={i} className="flex items-center gap-1">
                  {i > 0 && (
                    <Icon name="chevRight" size={10} color="var(--n-400)" />
                  )}
                  <span
                    className={
                      i === crumbs.length - 1 ? "text-n-700" : "text-n-500"
                    }
                  >
                    {c}
                  </span>
                </span>
              ))}
            </div>
          )}
          <div className="flex items-baseline gap-2.5">
            <h1 className="m-0 text-[18px] font-semibold leading-[1.2] tracking-[-0.3px] text-n-900">
              {title}
            </h1>
            {subtitle && (
              <div className="text-[12px] text-n-500">{subtitle}</div>
            )}
          </div>
        </div>
        {right && <div className="flex items-center gap-1.5">{right}</div>}
      </div>
      {tabs && tabs.length > 0 && (
        <div className="-mb-px mt-1 flex gap-0">
          {tabs.map((t) => {
            const on = t.key === activeTab;
            return (
              <button
                key={t.key}
                type="button"
                onClick={() => onTabChange?.(t.key)}
                className={`flex cursor-pointer items-center gap-1.5 border-b-2 px-3.5 py-2 text-[13px] ${
                  on
                    ? "border-n-900 font-semibold text-n-900"
                    : "border-transparent font-medium text-n-500 hover:text-n-700"
                }`}
              >
                {t.label}
                {t.count != null && (
                  <span
                    className={`rounded-full px-1.5 py-0 font-mono text-[10px] font-semibold ${
                      on ? "bg-n-900 text-white" : "bg-n-100 text-n-500"
                    }`}
                  >
                    {t.count}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      )}
    </header>
  );
}
